import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {UnsafeHTMLDiv} from '@github-ui/safe-html/UnsafeHTML'
import {useCreateScribeArtifact, useScribeArtifacts} from '@github-ui/scribe-artifacts'
import type {ScribeArtifact} from '@github-ui/scribe-artifacts/types'
import {ArrowLeftIcon, ChevronDownIcon, ChevronRightIcon, LinkIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, CounterLabel, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import {TEST_IDS} from '../../constants/test-ids'
import type {ArtifactsSectionQuery as ArtifactsSectionQueryType} from './__generated__/ArtifactsSectionQuery.graphql'
import {DerivedArtifactRow, PersistedArtifactRow} from './ArtifactRow'
import styles from './ArtifactsSection.module.css'
import type {DerivedArtifact, IssueArtifact} from './types'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ArtifactsSectionQuery = graphql`
  query ArtifactsSectionQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        databaseId
      }
    }
  }
`

type SelectedArtifact = {
  name: string
  bodyHTML: string
}

type ArtifactsSectionProps = {
  issueId: string
  derivedArtifacts?: DerivedArtifact[]
  onArtifactDelete?: (id: number) => void
  onDerivedDismiss?: (artifact: DerivedArtifact) => void
}

const EMPTY_DERIVED: DerivedArtifact[] = []

function ArtifactsSectionContent({
  issueId,
  derivedArtifacts = EMPTY_DERIVED,
  onArtifactDelete,
  onDerivedDismiss,
}: ArtifactsSectionProps) {
  const data = useLazyLoadQuery<ArtifactsSectionQueryType>(ArtifactsSectionQuery, {id: issueId})
  const issueDatabaseId = data.node?.databaseId ?? 0
  const [isExpanded, setIsExpanded] = useState<boolean | null>(null)
  const [selectedArtifact, setSelectedArtifact] = useState<SelectedArtifact | null>(null)
  const artifactReturnFocusRef = useRef<HTMLElement | null>(null)
  const backButtonRef = useRef<HTMLButtonElement | null>(null)
  const detailTitleId = useId()

  const scribeQuery = useScribeArtifacts({
    resourceType: 'issue',
    resourceId: issueDatabaseId,
    enabled: issueDatabaseId > 0,
  })
  const {mutate: createArtifact} = useCreateScribeArtifact({resourceType: 'issue', resourceId: issueDatabaseId})

  const persistedArtifacts = useMemo<IssueArtifact[]>(
    () =>
      (scribeQuery.data ?? []).map((artifact: ScribeArtifact, index) => ({
        id: typeof artifact.id === 'number' ? artifact.id : index + 1,
        number: artifact.number ?? index + 1,
        type: artifact.type === 'plan' || artifact.type === 'link' ? artifact.type : 'generic',
        name: artifact.name,
        body: artifact.body ?? '',
        bodyHTML: artifact.body_html ?? undefined,
        actor: {login: artifact.creator?.login ?? '', avatarUrl: ''},
        createdAt: artifact.created_at ?? '',
        updatedAt: artifact.updated_at ?? '',
      })),
    [scribeQuery.data],
  )

  const artifactCount = persistedArtifacts.length + derivedArtifacts.length

  const handleAddLink = useCallback(() => {
    const url = window.prompt('Link URL')
    if (!url) return
    const name = window.prompt('Link title', url) ?? url
    createArtifact({name, body: url, type: 'link'})
  }, [createArtifact])

  const effectiveExpanded = isExpanded ?? artifactCount > 0

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!effectiveExpanded)
  }, [effectiveExpanded])

  const handleArtifactSelect = useCallback((artifact: IssueArtifact) => {
    if (!artifact.bodyHTML) return
    artifactReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setSelectedArtifact({
      name: artifact.name,
      bodyHTML: artifact.bodyHTML,
    })
  }, [])

  const handleBack = useCallback(() => {
    setSelectedArtifact(null)
    requestAnimationFrame(() => artifactReturnFocusRef.current?.focus())
  }, [])

  useEffect(() => {
    if (!selectedArtifact) return
    const handle = requestAnimationFrame(() => backButtonRef.current?.focus())
    return () => cancelAnimationFrame(handle)
  }, [selectedArtifact])

  const inlineAvatars = isFeatureEnabled('issue_inline_avatars')
  const isListHidden = selectedArtifact || !effectiveExpanded
  const isDetailHidden = !selectedArtifact

  return (
    <div
      className={clsx(styles.Container, inlineAvatars && styles.inlineAvatars)}
      data-testid={TEST_IDS.issueArtifactsSection}
    >
      <div className={styles.Header}>
        {selectedArtifact ? (
          <div className={styles.TitleGroup}>
            <IconButton
              ref={backButtonRef}
              icon={ArrowLeftIcon}
              variant="invisible"
              size="small"
              aria-label="Back to artifacts list"
              onClick={handleBack}
            />
            <span id={detailTitleId} className={styles.TitleLabel}>
              {selectedArtifact.name}
            </span>
          </div>
        ) : (
          <>
            <div className={styles.TitleGroup}>
              <IconButton
                icon={effectiveExpanded ? ChevronDownIcon : ChevronRightIcon}
                variant="invisible"
                size="small"
                aria-label={effectiveExpanded ? 'Collapse artifacts' : 'Expand artifacts'}
                aria-expanded={effectiveExpanded}
                onClick={toggleExpanded}
              />
              <span className={styles.TitleLabel}>Artifacts</span>
              {artifactCount > 0 && <CounterLabel>{artifactCount}</CounterLabel>}
            </div>
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton icon={PlusIcon} variant="invisible" size="small" aria-label="Add artifact" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay align="end">
                <ActionList>
                  <ActionList.Item onSelect={handleAddLink}>
                    <ActionList.LeadingVisual>
                      <LinkIcon />
                    </ActionList.LeadingVisual>
                    Add link
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </>
        )}
      </div>

      <div
        aria-hidden={isListHidden ? true : undefined}
        className={clsx(styles.Body, isListHidden && styles.collapsed)}
        inert={isListHidden ? true : undefined}
      >
        <div className={styles.BodyInner}>
          {artifactCount === 0 ? (
            <div className={styles.EmptyState}>Add files, links, or plans to this issue.</div>
          ) : (
            <ul className={styles.List} aria-label="Artifacts">
              {persistedArtifacts.map(artifact => (
                <li key={artifact.id}>
                  <PersistedArtifactRow
                    artifact={artifact}
                    onSelect={artifact.type !== 'link' && artifact.bodyHTML ? handleArtifactSelect : undefined}
                    onDelete={onArtifactDelete}
                  />
                </li>
              ))}
              {derivedArtifacts.map(artifact => (
                <li key={artifact.derivedId}>
                  <DerivedArtifactRow artifact={artifact} onDismiss={onDerivedDismiss} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div
        aria-hidden={isDetailHidden ? true : undefined}
        className={clsx(styles.Body, isDetailHidden && styles.collapsed)}
        inert={isDetailHidden ? true : undefined}
      >
        <div className={styles.DetailBody}>
          {selectedArtifact && (
            <UnsafeHTMLDiv
              aria-labelledby={detailTitleId}
              className={clsx(styles.DetailContent, 'markdown-body')}
              html={selectedArtifact.bodyHTML}
              role="region"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export function ArtifactsSection(props: ArtifactsSectionProps) {
  if (!isFeatureEnabled('issue_artifacts_section')) return null

  return (
    <ErrorBoundary boundaryName="ArtifactsSection" fallback={null} critical={false}>
      <Suspense fallback={null}>
        <ArtifactsSectionContent {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}
